import { create } from 'zustand';
import api from '@/features/api/axios';
import { logger } from '@/features/help/utils/logger';
import { FormStructure, FormField, FieldValue } from '../types/forms';
import { toast } from 'react-hot-toast';

import { useValidationStore } from './validation.store';
import { useSignerStore } from './signer.store';
import { AxiosError } from 'axios';

interface ErrorResponse {
  message?: string;
}

interface ApplicationStore {
  structure: FormStructure | null;
  loading: boolean;
  error: string | null;
  recordId: string | null;
  isEditable: boolean;
  hasMappings: boolean;
  primaryCrmObject: string | null;
  crmId: string | null;

  viewableFields: string[];
  editableFields: string[];
  files: Record<string, File>;
  recordActionStatus: string | null;
  termsConsent: boolean;
  isBrokerTrialEnded: boolean;
  userShortId: string | null;
  userId: string | null;
  pdfOnly: boolean;

  setTermsConsent: (consent: boolean) => void;
  setPrimaryCrmObject: (primaryCrmObject: string) => void;

  // Structure Management
  initializeFromApiPayload: (payload: any) => void;
  setStructure: (structure: FormStructure) => void;
  updateStructure: (newStructure: FormStructure) => void;

  // Field Management
  handleFieldUpdate: (fieldId: string, updates: Partial<FormField>) => void;
  handleFieldValueChange: (fieldId: string, value: FieldValue) => void;
  updateFile: (fieldId: string, file: File) => void;

  // Form State
  setIsEditable: (isEditable: boolean) => void;
  submitForm: (captchaToken: string, mappingShortId: string) => Promise<{ success: boolean }>;

  // State Management
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setBrokerTrialEnded: (ended: boolean) => void;

  // Getters
  getUserId: () => string;
}

// Track the last submission time to prevent duplicate submissions
let lastSubmissionTime = 0;
const SUBMISSION_DEBOUNCE_MS = 2000; // 2 seconds

// Helper functions for invalid fields removed - now handled by validationStore

// NEW HELPER FUNCTION FOR CLEARING FILES
const _clearFilesForFieldId = (
  currentFiles: Record<string, File>,
  fieldIdToRemove: string
): Record<string, File> => {
  const newFilesMap = { ...currentFiles };
  for (const key in newFilesMap) {
    if (key === fieldIdToRemove || key.startsWith(`${fieldIdToRemove}_`)) {
      delete newFilesMap[key];
    }
  }

  return newFilesMap;
};
// END OF HELPER FUNCTION FOR CLEARING FILES

export const useApplicationStore = create<ApplicationStore>((set, get) => ({
  structure: null,
  loading: false,
  error: null,
  recordId: null,
  isEditable: true,
  hasMappings: false,
  primaryCrmObject: null,
  crmId: null,

  viewableFields: [],
  editableFields: [],
  files: {},
  recordActionStatus: null,
  termsConsent: false,
  isBrokerTrialEnded: false,
  userShortId: null,
  userId: null,
  pdfOnly: false,

  // Computed Values

  // Purpose: Initialize application state from API payload (used after redirect logic)
  initializeFromApiPayload: (payload) => {

    const receivedStructure = payload.structure;
    const recordStatus = payload.recordStatus || null;
    const currentSignerIndex = payload.currentSignerIndex || 1;
    const userIdFromResponse = payload.userId || null;

    // Initialize signer store first
    const signerStore = useSignerStore.getState();
    signerStore.initializeFromApiPayload({
      recordStatus,
      currentSignerIndex,
      signerShortId: payload.signerShortId,
    });

    // Calculate permissions for the current signer using actual signer index
    const { viewableFields, editableFields } = signerStore.updateSignerPermissions(
      currentSignerIndex,
      receivedStructure
    );

    // Update application store state
    set({
      structure: receivedStructure,
      viewableFields,
      editableFields,
      recordActionStatus: recordStatus,
      error: null,
      hasMappings: true,
      userId: userIdFromResponse ? String(userIdFromResponse) : null,
      recordId: payload.recordId || null,
      userShortId: payload.userShortId || null,
      primaryCrmObject: payload.primaryCrmObject || null,
      crmId: payload.crmId || null,
      pdfOnly: payload.pdfOnly || false,
    });


  },

  setStructure: (structure) => set({ structure }),

  updateStructure: (newStructure) => set({ structure: newStructure }),

  handleFieldUpdate: (fieldId: string, updates: Partial<FormField>) => {
    const { structure } = get();
    if (!structure?.pages) return;

    const updatedPages = structure.pages.map((page) => ({
      ...page,
      sections: page.sections.map((section) => ({
        ...section,
        fields: section.fields.map((field) =>
          field.id === fieldId ? { ...field, ...updates } : field
        ),
      })),
    }));

    set({ structure: { ...structure, pages: updatedPages } });
  },

  /**
   * Update a field's value
   */
  handleFieldValueChange: (fieldId, value) => {
    set((state) => {
      if (!state.structure) {
        return {};
      }

      let fieldToUpdate: FormField | undefined;
      let fieldTypeForClearing = '';
      const newPages = state.structure.pages.map((page) => ({
        ...page,
        sections: page.sections.map((section) => ({
          ...section,
          fields: section.fields.map((field) => {
            if (field.id === fieldId) {
              fieldToUpdate = field; // Keep a reference to the original field
              fieldTypeForClearing = field.type;

              

              const updatedField = { ...field, value };


              return updatedField;
            }
            return field;
          }),
        })),
      }));

      const newStructure = { ...state.structure, pages: newPages };

      if (fieldToUpdate) {
        useValidationStore.getState().validateField(fieldToUpdate, value, fieldId);
      }

      // Handle file updates separately
      let files = state.files;
      if (fieldTypeForClearing === 'file') {
        files = _clearFilesForFieldId(state.files, fieldId);
        if (Array.isArray(value)) {
          const newFiles: Record<string, File> = {};
          (value as { name: string; contentType: string; file?: File }[]).forEach(
            (fileItem, index) => {
              if (fileItem.file instanceof File) {
                const fileKey = `${fieldId}_${index}`;
                newFiles[fileKey] = fileItem.file;
              }
            }
          );
          files = { ...files, ...newFiles };
        } else if (value instanceof File) {
          files = { ...files, [fieldId]: value };
        }
      }

      return { structure: newStructure, files };
    });
  },

  updateFile: (fieldId: string, file: File) => {
    const files = { ...get().files };
    files[fieldId] = file;
    set({ files });

  },

  setIsEditable: (isEditable) => {
    set({ isEditable });
  },

  // Validation functions removed - now handled by validationStore

  /**
   * Submit the form data to the server
   */
  submitForm: async (captchaToken: string, mappingShortId: string) => {
    const { structure, recordId, files, userShortId, primaryCrmObject, pdfOnly } = get();
    const signerStoreState = useSignerStore.getState();
    const { signerShortId } = signerStoreState;
    

    // For PDF-only submissions, signerShortId is not required
    const requiredFieldsMissing = !structure || !userShortId || !recordId || (!pdfOnly && !signerShortId);
    
    if (requiredFieldsMissing) {
      const errorMessage = 'Required form data is not available.';
      logger.error('Application Store', 'submitForm: Pre-submission check failed', {
        hasStructure: !!structure,
        hasRecordId: !!recordId,
        hasUserShortId: !!userShortId,
        hasSignerShortId: !!signerShortId,
        hasPrimaryCrmObject: !!primaryCrmObject,
        pdfOnly,
      });
      toast.error(errorMessage);
      return { success: false };
    }

    // Debounce submission to prevent accidental double-clicks
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_DEBOUNCE_MS) {
      logger.warn('Application Store', 'submitForm: Submission attempt blocked by debounce');
      toast.error('Please wait a moment before submitting again.');
      return { success: false };
    }
    lastSubmissionTime = now;

    set({ loading: true, error: null });

    const formData = new FormData();
    formData.append('structure', JSON.stringify(structure));
    formData.append('captchaToken', captchaToken);
    formData.append('mappingShortId', mappingShortId);
    if (recordId) {
      formData.append('recordId', recordId);
    }
    formData.append('userShortId', userShortId);
    if (signerShortId) {
      formData.append('signerShortId', signerShortId);
    }
    if (primaryCrmObject) {
      formData.append('primaryCrmObject', primaryCrmObject);
    }
    if (pdfOnly) {
      formData.append('pdfOnly', 'true');
    }

    // Append files to the FormData - these will be handled separately by the backend
    const fileList: File[] = Object.values(files);
    const fileIds: string[] = Object.keys(files);

    if (fileList.length > 0) {
      fileList.forEach((file) => {
        formData.append('files', file);
      });
      formData.append('fileIds', JSON.stringify(fileIds));
    }

    try {
      const correlationId = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
        ? crypto.randomUUID()
        : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}-${Math.random()
            .toString(16)
            .slice(2, 10)}`;
      const submissionUrl = `/api/record-actions/submit/${userShortId}/${mappingShortId}/${recordId}${
        signerShortId ? `/${signerShortId}` : ''
      }`;
      const { data: _data } = await api.post(
        submissionUrl,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'x-recaptcha-token': captchaToken,
            'x-correlation-id': correlationId,
          },
        }
      );

      set({
        loading: false,
      });

      return {
        success: true,
      };
    } catch (error) {
      set({ loading: false });
      const { recordId, userShortId } = get();
      const { signerShortId } = signerStoreState;
      logger.error('Application Store', 'Form submission failed', {
        recordId,
        userShortId,
        signerShortId,
        error,
        axiosError: error instanceof AxiosError ? error.response?.data : 'N/A',
      });

      // Show a generic error toast to the user
      toast.error(
        'An unexpected error occurred during submission. If the problem persists, please contact support.'
      );

      // Log the specific error message for debugging, but don't set it in the store to avoid UI hanging
      if (error instanceof AxiosError && error.response?.data) {
        const errResponse = error.response.data as ErrorResponse;
        const specificMessage = errResponse.message || 'No specific error message provided.';
        logger.error('Application Store', 'Detailed submission error', { specificMessage });
      }

      return { success: false };
    }
  },

  // State Management
  setLoading: (loading) => {
    set({ loading });
  },

  setError: (error) => {
    // Only log actual errors, not null (error clearing)
    if (error) {
      logger.error('SubmissionFlow', 'Error set', { error });
    }
    set({ error });
  },

  setBrokerTrialEnded: (ended) => {
    set({ isBrokerTrialEnded: ended });
  },

  // Navigation functions moved to navigationStore

  setTermsConsent: (consent) => {
    set({ termsConsent: consent });
  },

  setPrimaryCrmObject: (primaryCrmObject) => {
    set({ primaryCrmObject });
  },

  getUserId: () => {
    const { userId } = get();
    return userId || 'unknown';
  },
}));
